/*
 * Copyright (C) 2021 IHS Markit.
 * All Rights Reserved
 *
 * NOTICE: All information contained herein is, and remains
 * the property of IHS Markit and its suppliers,
 * if any. The intellectual and technical concepts contained
 * herein are proprietary to IHS Markit and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from IHS Markit.
 */

import {Injectable} from '@angular/core';
import {SectorEnum} from '../modals/SectorEnum';
import {SubSectorEnum} from '../modals/SubSectorEnum';
import {Index} from '../modals/Index';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';

@Injectable()
export class UtilService {

  mockData = {
    'Documentation/iBoxx/Rules_Benchmark/US/USD/Markit iBoxx USD FRN Investment Grade Corporates Index Guide (last updated March 2022).pdf': 'iBoxx USD FRN',
    'Documentation/iBoxx/Rules_Benchmark/US/USD/Markit iBoxx USD Infrastructure Index Guide (last updated March 2022).pdf': 'iBoxx Infrastructure',
    'Documentation/iBoxx/Rules_Benchmark/US/USD/Markit iBoxx USD Leveraged Loan Index (last updated February 2019).pdf': 'iBoxx USD Leveraged Loan',
    'Documentation/iBoxx/Rules_Benchmark/US/USD/Markit iBoxx USD Regulated Utilities Index Guide (last updated March 2022).pdf': null,
    'Documentation/iBoxx/Rules_Benchmark/US/USD/Short Markit iBoxx $ Treasuries Total Return Index Repo Cost and Rebalancing Cost (last updated March 2022).xlsx': null,
    'Documentation/iBoxx/Rules_Benchmark/US/USD/iBoxx MSCI ESG USD Emerging Markets Sovereigns Quality Weighted Index Guide (last updated November 2021).pdf': 'iBoxx Quality Indices',
    'Documentation/iBoxx/Rules_Benchmark/US/USD/iBoxx USD Belt & Road Index Guide (last updated March 2022).pdf': 'iBoxx USD Belt and Road',
    'Documentation/iBoxx/Rules_Benchmark/US/USD/iBoxx USD Emerging Markets Sovereigns Strategic Risk Adjusted Index Guide (last updated September 2019).pdf': 'iBoxx ECR',
    'Documentation/iBoxx/Rules_Benchmark/US/USD/iBoxx USD Senior Corporates Investment Grade ESG Factor Weighted Index Guide (last updated October 2021).pdf': null,
    'Headline-Indices/': null,
    'Headline-Indices/iBoxx/': null,
    'Headline-Indices/iBoxx/Headlines_Indices.txt': null,
    'Headline-Indices/iBoxx/Headlines_Indices_1.txt': null,
    'News/': null,
  };
  maturity = ['years1To3', 'years1To5', 'years1To10', 'years1To15', 'years1To20', 'years3To5', 'years5To7', 'years5To10', 'years5To15', 'years5Plus',
    'years7To10', 'years7Plus', 'years10To15', 'years10Plus', 'years15To20', 'years15To25', 'years15Plus', 'years20To25', 'years25To30', 'years25Plus',
    'years30Plus'];

  public formatNumber(input: string | number): string | number {
    if (input) {
      input = typeof input === 'number' ? input.toString() : input;
      return input.replace(/(.)(?=(.{3})+$)/g, '$1,');
    }
    return input;
  }

  public formatCurrency(input: number): number | null {
    if (input) {
      return input.toString().length > 7 ? Math.round(input / 1000000) : Math.round(input / 1000);
    }
    return input;
  }

  public formatDate(date: string): string {
    const derivedDate = new Date(date).toUTCString().split(' ');
    return derivedDate[1] + '-' + derivedDate[2] + '-' + derivedDate[3];
  }

  public differenceInDate(d1: Date, d2: Date): number{
    return Math.floor((Date.UTC(d1.getFullYear(), d1.getMonth(), d1.getDate()) - Date.UTC(d2.getFullYear(), d2.getMonth(), d2.getDate()))
      / (1000 * 60 * 60 * 24));
  }

  public addDaysTo(date: Date, days: number): Date{
    date.setDate(date.getDate() + days);
    return date;
  }

  public getBusinessDay(date: Date): Date{
    switch (date.getDay()){
      case 6: date.setDate(date.getDate() + 2);
        break;
      case 0: date.setDate(date.getDate() + 1);
        break;
    }
    return date;
  }

  public isNumber(value: any): boolean {
    return typeof value === 'number';
  }

  public compareByWorkflow(a: any, b: any): number{
    const aContainsUSEod = a.id.includes('us_eod');
    const aContainsEUEod = a.id.includes('eu_eod');
    const aContainsFEEod = a.id.includes('fe_eod');
    const bContainsUSEod = b.id.includes('us_eod');
    const bContainsEUEod = b.id.includes('eu_eod');
    const bContainsFEEod = b.id.includes('fe_eod');

    if (aContainsUSEod || bContainsUSEod) {
      if (aContainsUSEod) { return -1; }
      return 1;
    }
    if (aContainsEUEod || bContainsEUEod) {
      if (aContainsEUEod) { return -1; }
      return 1;
    }
    if (aContainsFEEod || bContainsFEEod) {
      if (aContainsFEEod) { return -1; }
      return 1;
    }
    return 0;
  }

  public getSector(index: any): string | undefined {
    if (index.level1 === 'Collateralized' || index.level3 === 'Collateralized' || index.level2 === 'Collateralized') {
      return SectorEnum.Collateralized;
    }
    if (index.level1 === 'Corporates' || index.level3 === 'Corporates' || index.level2 === 'Corporates') {
      return SectorEnum.Corporates;
    }
    if (index.level2 === 'Financials' || index.level4 === 'Financials' || index.level3 === 'Financials') {
      return SectorEnum.Financials;
    }
    if (index.level2 === 'Non-Financials' || index.level4 === 'Non-Financials' || index.level3 === 'Non-Financials') {
      return SectorEnum.Non_Financials;
    }
    if (index.level1 === 'Gilts') {
      return SectorEnum.Gilts;
    }
    if (index.level1 === 'Non-Gilts') {
      return SectorEnum.Non_Gilts;
    }
    if (index.level2 === 'Treasuries') {
      return SectorEnum.Treasuries;
    }
    if (index.level1 === 'Sovereigns' || index.level3 === 'Sovereigns' || index.level4 === 'Sovereigns') {
      return SectorEnum.Sovereigns;
    }
    if (index.level1 === 'Non-Sovereigns' || index.level2 === 'Non-Treasuries') {
      return SectorEnum.Non_sovereigns;
    }
    if (index.level2 === 'Non-Government') {
      return SectorEnum.Non_Government;
    }
    if (index.level3 === 'Sovereigns & Sub-Sovereigns' || index.level2 === 'Sovereigns & Sub-Sovereigns' || index.level1 === 'Sovereigns & Sub-Sovereigns') {
      return SectorEnum.Sovereigns_SubSovereigns;
    }
    if (index.level1 === 'Sub-Sovereigns' || index.level2 === 'Sub-Sovereigns' || index.level3 === 'Sub-Sovereigns') {
      return SectorEnum.Sub_Sovereigns;
    }
    if (index.level2 === 'Government') {
      return SectorEnum.Government;
    }
    return undefined;
  }

  public findInSectorList(index: any): string[] {
    const sectorResult: string[] = [];
    if (index.level1 === 'Collateralized' || index.level3 === 'Collateralized' || index.level2 === 'Collateralized') {
      sectorResult.push(SectorEnum.Collateralized);
    }
    if (index.level1 === 'Corporates' || index.level3 === 'Corporates' || index.level2 === 'Corporates') {
      sectorResult.push(SectorEnum.Corporates);
    }
    if (index.level2 === 'Financials' || index.level4 === 'Financials' || index.level3 === 'Financials') {
      sectorResult.push(SectorEnum.Financials);
    }
    if (index.level2 === 'Non-Financials' || index.level4 === 'Non-Financials' || index.level3 === 'Non-Financials') {
      sectorResult.push(SectorEnum.Non_Financials);
    }
    if (index.level1 === 'Gilts') {
      sectorResult.push(SectorEnum.Gilts);
    }
    if (index.level1 === 'Non-Gilts') {
      sectorResult.push(SectorEnum.Non_Gilts);
    }
    if (index.level2 === 'Treasuries') {
      sectorResult.push(SectorEnum.Treasuries);
    }
    if (index.level1 === 'Sovereigns' || index.level3 === 'Sovereigns' || index.level4 === 'Sovereigns') {
      sectorResult.push(SectorEnum.Sovereigns);
    }
    if (index.level1 === 'Non-Sovereigns' || index.level2 === 'Non-Treasuries') {
      sectorResult.push(SectorEnum.Non_sovereigns);
    }
    if (index.level2 === 'Non-Government') {
      sectorResult.push(SectorEnum.Non_Government);
    }
    if (index.level3 === 'Sovereigns & Sub-Sovereigns' || index.level2 === 'Sovereigns & Sub-Sovereigns' || index.level1 === 'Sovereigns & Sub-Sovereigns') {
      sectorResult.push(SectorEnum.Sovereigns_SubSovereigns);
    }
    if (index.level1 === 'Sub-Sovereigns' || index.level2 === 'Sub-Sovereigns' || index.level3 === 'Sub-Sovereigns') {
      sectorResult.push(SectorEnum.Sub_Sovereigns);
    }
    if (index.level2 === 'Government') {
      sectorResult.push(SectorEnum.Government);
    }
    return sectorResult;
  }

  public getRecordsForRegion(regions: string, batchData: Index[]): any | undefined {
    if (regions) {
      let result: Index[] = [];
      regions.split('~').forEach((region: string) => {
        if (region === 'Global') {
          result = result.concat(batchData.filter((data: any) => {
            if (this.getUniqueRegions(data.productRegion) === region || this.getUniqueRegions(data.productRegion).split(',').length > 1) {
              return data;
            }
          }));
        } else {
          result = result.concat(batchData.filter((data: any) => {
            if (this.getUniqueRegions(data.productRegion) === region.split('_').join(' ')) {
              return data;
            }
          }));
        }
      });

      return result.filter((elem: any, index: number, self: any) => {
        return index === self.indexOf(elem) && isNotNullOrUndefined(elem);
      });
    } else {
      return batchData;
    }
  }

  public getUniqueRegions(region: string): string{
    return Array.from(new Set(region.split(','))).join(',');
  }

  public getRecordsForRating(rating: string, batchData: Index[]): any | undefined {
    let result: Index[] = [];
    rating.split('~').forEach((rate: string) => {
      if (rate === '*') {
        result = result.concat(batchData.filter((data: any) => {
          if (data.rating === rate || data.rating === null) {
            return data;
          }
        }));
      } else if (rate === 'Investment Grade') {
        rate = rate.split(' ').map((n) => n[0]).join('');
        result = result.concat(batchData.filter((data: any) => {
          if (data.ratingCategory === rate) {
            return data;
          }
        }));
      } else if (rate === 'High Yield') {
        rate = rate.split(' ').map((n) => n[0]).join('');
        result = result.concat(batchData.filter((data: any) => {
          if (data.ratingCategory === rate) {
            return data;
          }
        }));
      } else {
        result = result.concat(batchData.filter((data: any) => {
          if (data.rating === rate) {
            return data;
          }
        }));
      }
    });

    return result.filter((elem: any, index: number, self: any) => {
      return index === self.indexOf(elem) && isNotNullOrUndefined(elem);
    });
  }

  public getSubSector(index: any): string | undefined {
    if (index.level2 === 'Covered' || index.level4 === 'Covered') {
      return SubSectorEnum.Covered;
    }
    if (index.level2 === 'Financials' || index.level4 === 'Financials' || index.level3 === 'Financials') {
      return SubSectorEnum.Financials;
    }
    if (index.level2 === 'Non-Financials' || index.level4 === 'Non-Financials' || index.level3 === 'Non-Financials') {
      return SubSectorEnum.Non_Financials;
    }
    if (index.level3 === 'Basic Materials' || index.level4 === 'Basic Materials' || index.level5 === 'Basic Materials') {
      return SubSectorEnum.Basic_Materials;
    }
    if (index.level3 === 'Consumer Goods' || index.level4 === 'Consumer Goods' || index.level5 === 'Consumer Goods') {
      return SubSectorEnum.Consumer_Goods;
    }
    if (index.level3 === 'Consumer Services' || index.level4 === 'Consumer Services' || index.level5 === 'Consumer Services') {
      return SubSectorEnum.Consumer_Services;
    }
    if (index.level3 === 'Core Financials' || index.level4 === 'Core Financials' || index.level5 === 'Core Financials') {
      return SubSectorEnum.Core_Financials;
    }
    if (index.level3 === 'Financial Services' || index.level4 === 'Financial Services' || index.level5 === 'Financial Services') {
      return SubSectorEnum.Financial_Services;
    }
    if (index.level3 === 'Health Care' || index.level4 === 'Health Care' || index.level5 === 'Health Care') {
      return SubSectorEnum.Health_Care;
    }
    if (index.level3 === 'Industrials' || index.level4 === 'Industrials' || index.level5 === 'Industrials') {
      return SubSectorEnum.Industrials;
    }
    if (index.level3 === 'Oil & Gas' || index.level4 === 'Oil & Gas' || index.level5 === 'Oil & Gas') {
      return SubSectorEnum.Oil_Gas;
    }
    if (index.level3 === 'Real Estate' || index.level4 === 'Real Estate' || index.level5 === 'Real Estate') {
      return SubSectorEnum.Real_Estate;
    }
    if (index.level3 === 'Technology' || index.level4 === 'Technology' || index.level5 === 'Technology') {
      return SubSectorEnum.Technology;
    }
    if (index.level3 === 'Telecommunications' || index.level4 === 'Telecommunications' || index.level5 === 'Telecommunications') {
      return SubSectorEnum.Telecommunications;
    }
    if (index.level3 === 'Utilities' || index.level4 === 'Utilities' || index.level5 === 'Utilities') {
      return SubSectorEnum.Utilities;
    }
    if (index.level4 === 'Banks' || index.level5 === 'Banks' || index.level6 === 'Banks') {
      return SubSectorEnum.Banks;
    }
    return undefined;
  }

  downloadFile(csvData: any, filename: any): void {
    const blob = new Blob(['\ufeff' + csvData], {type: 'text/csv;charset=utf-8;'});
    const dwldLink = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const isSafariBrowser = navigator.userAgent.indexOf('Safari') !== -1 && navigator.userAgent.indexOf('Chrome') === -1;
    if (isSafariBrowser) {  // if Safari open in new window to save file with random filename.
      dwldLink.setAttribute('target', '_blank');
    }
    dwldLink.setAttribute('href', url);
    dwldLink.setAttribute('download', filename + '.csv');
    dwldLink.style.visibility = 'hidden';
    document.body.appendChild(dwldLink);
    dwldLink.click();
    document.body.removeChild(dwldLink);
  }

  convertToCSV(isin: string, indexName: string, headerList: any, objArray: any): string {
    let row = headerList.join(',');
    row += '\r\n';
    objArray.forEach((object: any) => {
      row += new Date(object.date).toLocaleString().split(',')[0] + ',';
      row += isin + ',' + indexName + ',';
      row += object.value;
      row += '\r\n';
    });

    return row;
  }

  convertConstituentDataToCSV(headerList: any, objArray: any): string {
    let row = headerList.join(',');
    row += '\r\n';
    objArray.forEach((object: any) => {
      row += new Date(object.asOf).toLocaleString().split(',')[0] + ',';
      row += new Date(object.rebalancingDate).toLocaleString().split(',')[0] + ',';
      row += (object.bondRebalancing ? object.bondRebalancing.issuer : object.loanRebalancing.issuer) + ',';
      row += (object.bondRebalancing ? object.bondRebalancing.coupon : object.loanRebalancing.coupon) + ',';
      row += (object.bondRebalancing ?
        new Date(object.bondRebalancing.workoutDate).toLocaleString().split(',')[0] :
        new Date(object.loanRebalancing.maturityDate).toLocaleString().split(',')[0]) + ',';
      row += (object.indexWeight * 100).toFixed(4);
      row += '\r\n';
    });

    return row;
  }

  convertMultipleIndexDataToCSV(header1: string[], headerList: string[], dataArray: any): string{
    let row = header1.join(',');
    row += '\r\n';
    row += '\r\n';
    row += headerList.join(',');
    row += '\r\n';
    // tslint:disable-next-line:prefer-for-of
    for (let i = 0; i < dataArray.Date.length; i++){
      for (const header of headerList){
        if (header === 'Date') {
          row += new Date(dataArray[header][i]).toLocaleString().split(',')[0] + ',';
        }else{
          row += (dataArray[header][i] ? dataArray[header][i] : '') + ',';
        }
      }
      row += '\r\n';
    }
    return row;
  }

  public getRecordsForInvestmentType(investmentType: string, batchData: Index[]): any | undefined {
    if (investmentType) {
      let result: Index[] = [];
      investmentType.split('~').forEach((investType: string) => {
        result = result.concat(batchData.filter((data: any) => {
          if (data.investmentType && data.investmentType.toUpperCase().includes(investType.toUpperCase())) {
            return data;
          }
        }));
      });
      return result.filter((elem: any, index: number, self: any) => {
        return index === self.indexOf(elem) && isNotNullOrUndefined(elem);
      });
    } else {
      return batchData;
    }
  }

  public getRecordsForIndexCurrency(indexCurrency: string, batchData: Index[]): any | undefined {
    if (indexCurrency) {
      let result: Index[] = [];
      indexCurrency.split('~').forEach((currency: string) => {
        result = result.concat(batchData.filter((data: any) => data.indexCurrency === currency));
      });
      return result.filter((elem: any, index: number, self: any) => {
        return index === self.indexOf(elem) && isNotNullOrUndefined(elem);
      });
    } else {
      return batchData;
    }
  }

  public getRecordsForCurrencyForEMIX(currencies: string, batchData: Index[]): any | undefined {
    if (currencies) {
      let result: Index[] = [];
      currencies.split('~').forEach((currency: string) => {
        result = result.concat(batchData.filter((data: any) => data.currency === currency));
      });
      return result.filter((elem: any, index: number, self: any) => {
        return index === self.indexOf(elem) && isNotNullOrUndefined(elem);
      });
    } else {
      return batchData;
    }
  }

  public getRecordsForMasterIDForEMIX(masterIDs: string, batchData: Index[]): any | undefined {
    if (masterIDs) {
      let result: Index[] = [];
      masterIDs.split('~').forEach((id: string) => {
        result = result.concat(batchData.filter((data: any) => data.indexMaster.id === id));
      });
      return result.filter((elem: any, index: number, self: any) => {
        return index === self.indexOf(elem) && isNotNullOrUndefined(elem);
      });
    } else {
      return batchData;
    }
  }

  public getRecordsForIndexNameForEMIX(indexNames: string, batchData: Index[]): any | undefined {
    if (indexNames) {
      let result: Index[] = [];
      indexNames.split('~').forEach((indexName: string) => {
        result = result.concat(batchData.filter((data: any) => data.indexName === indexName));
      });
      return result.filter((elem: any, index: number, self: any) => {
        return index === self.indexOf(elem) && isNotNullOrUndefined(elem);
      });
    } else {
      return batchData;
    }
  }

  public getRecordsForBaseCurrency(fxVersion: string, batchData: Index[]): any | undefined {
    if (fxVersion) {
      let result: Index[] = [];
      fxVersion.split('~').forEach((fx: string) => {
        result = result.concat(batchData.filter((data: any) => {
          if (data.fxVersion && data.fxVersion.toUpperCase() === fx.toUpperCase()) {
            return data;
          }
        }));
      });
      return result.filter((elem: any, index: number, self: any) => {
        return index === self.indexOf(elem) && isNotNullOrUndefined(elem);
      });
    } else {
      return batchData;
    }
  }

  public getRecordsForSector(sectors: string, batchData: Index[]): any | undefined {
    if (sectors) {
      let result: Index[] = [];
      sectors.split('~').forEach((sector: string) => {
        if (sector === '(unassigned)') {
          result = result.concat(batchData.filter((data: any) => this.getSector(data) === undefined));
        } else {
          result = result.concat(batchData.filter((data: any) => this.findInSectorList(data).includes(sector)));
        }
      });
      return result.filter((elem: any, index: number, self: any) => {
        return index === self.indexOf(elem) && isNotNullOrUndefined(elem);
      });
    } else {
      return batchData;
    }
  }

  public getRecordsForMaturity(maturities: string, batchData: Index[]): any | undefined {
    if (maturities) {
      let result: Index[] = [];
      maturities.split('~').forEach((maturity: string) => {
        if (maturity === '(unassigned)') {
          let maturityIndex: Index[] = [];
          this.maturity.forEach((m: string) => {
            maturityIndex = maturityIndex.concat(batchData.filter((data: any) => data[m]));
          });
          result = result.concat(batchData.filter((item) => maturityIndex.indexOf(item) === -1));
        } else {
          result = result.concat(batchData.filter((data: any) => data[maturity] === true));
        }
      });
      return result.filter((elem: any, index: number, self: any) => {
        return index === self.indexOf(elem) && isNotNullOrUndefined(elem);
      });
    } else {
      return batchData;
    }
  }

  public getRecordsForSubSector(subSectors: string, batchData: Index[]): any | undefined {
    if (subSectors) {
      let result: Index[] = [];
      subSectors.split('~').forEach((subSector: string) => {
        if (subSector === '(unassigned)') {
          result = result.concat(batchData.filter((data: any) => this.getSubSector(data) === undefined));
        } else {
          result = result.concat(batchData.filter((data: any) => this.getSubSector(data) === subSector));
        }
      });
      return result.filter((elem: any, index: number, self: any) => {
        return index === self.indexOf(elem) && isNotNullOrUndefined(elem);
      });
    } else {
      return batchData;
    }
  }

  public getRecordsForIndexFamily(indexFamily: string, batchData: Index[]): any | undefined {
    if (indexFamily) {
      let result: Index[] = [];
      indexFamily.split('~').forEach((family: string) => {
        result = result.concat(batchData.filter((data: any) => {
          if (data.productName && data.productName.toUpperCase().includes(family.toUpperCase())) {
            return data;
          }
        }));
      });
      return result.filter((elem: any, index: number, self: any) => {
        return index === self.indexOf(elem) && isNotNullOrUndefined(elem);
      });
    } else {
      return batchData;
    }
  }

  public getRecordsForIndexFamilyForEMIX(indexFamily: string, batchData: Index[]): any | undefined {
    if (indexFamily) {
      let result: Index[] = [];
      indexFamily.split('~').forEach((family: string) => {
        result = result.concat(batchData.filter((data: any) => {
          if (data.indexMaster.indexFamily.id === family) {
            return data;
          }
        }));
      });
      return result.filter((elem: any, index: number, self: any) => {
        return index === self.indexOf(elem) && isNotNullOrUndefined(elem);
      });
    } else {
      return batchData;
    }
  }

  agGridSizeColumnsToFit(gridApi: any): void {
    if (gridApi) {
      gridApi.sizeColumnsToFit();
    }
  }

  highlightSideMenuNavFor(pageName: string): void {
    switch (pageName) {
      case 'iBoxx':
        this.activateIcon('markets');
        this.deactivateIcon('document-library');
        this.toggleSubMenu('Indices', 'iBoxx');
        break;
      case 'Carbon':
        this.activateIcon('markets');
        this.deactivateIcon('document-library');
        this.toggleSubMenu('Indices', 'Carbon');
        break;
      case 'EMIX':
        this.activateIcon('markets');
        this.deactivateIcon('document-library');
        this.toggleSubMenu('Indices', 'EMIX');
        break;
      case 'docs':
        this.activateIcon('document-library');
        this.deactivateIcon('markets');
        window.location.href.includes('iBoxx') ? this.toggleSubMenu('Documentation', 'iBoxx') :
          this.toggleSubMenu('Documentation', 'Carbon');
        break;
    }
  }

  activateIcon(icon: string): void {
    const elt = document.getElementsByClassName('nav-icon-' + icon + '-white');
    if (elt.length > 0) {
      elt[0].classList.add('nav-icon-' + icon + '-green');
      // @ts-ignore
      elt[0].parentElement.parentElement.parentElement.classList.add('active');
    }
  }

  deactivateIcon(icon: string): void {
    let elt = document.getElementsByClassName('nav-icon-' + icon + '-white');
    if (elt.length > 0) {
      elt[0].classList.remove('nav-icon-' + icon + '-green');
      // @ts-ignore
      elt[0].parentElement.parentElement.parentElement.classList.remove('active');
    }else{
      elt = document.getElementsByClassName('nav-icon-' + icon + '-green');
      if (elt.length > 0) {
        elt[0].classList.add('nav-icon-' + icon + '-white');
        // @ts-ignore
        elt[0].parentElement.parentElement.parentElement.classList.remove('active');
        elt[0].classList.remove('nav-icon-' + icon + '-green');
      }
    }
  }

  toggleSubMenu(menuItem: string, pageName: string): void {
    const elt = document.getElementsByClassName('sub-menu-list');
    // tslint:disable-next-line:prefer-for-of
    for (let i = 0; i < elt.length; i++) {
      // @ts-ignore
      if (elt[i].parentElement.parentElement.firstChild.innerText &&
        // @ts-ignore
        elt[i].parentElement.parentElement.firstChild.innerText.includes(menuItem) && elt[i].innerText.includes(pageName)) {
        elt[i].classList.add('sub-menu-active');
      } else {
        elt[i].classList.remove('sub-menu-active');
      }
    }
  }

  saveTabListFor(indexType: string, tabList: string[]): void{
    // @ts-ignore
    const tabs = JSON.parse(window.sessionStorage.getItem('tabList')) ? JSON.parse(window.sessionStorage.getItem('tabList')) :
      {
        all: [],
        futures: [],
        trs: [],
        esg: [],
      };
    tabs[indexType] = tabList;
    window.sessionStorage.setItem('tabList', JSON.stringify(tabs));
  }

  removeChartFilerOptionFor(id: string): void{
    // @ts-ignore
    const chartFilters = JSON.parse(localStorage.getItem('chartFilterOptions')) ? JSON.parse(localStorage.getItem('chartFilterOptions'))
      : [];

    const optionIndex = chartFilters.findIndex((filter: any) => filter.id === id);
    if (optionIndex > -1) {
      chartFilters.splice(optionIndex, 1);
    }
    localStorage.setItem('chartFilterOptions', JSON.stringify(chartFilters));
  }
}
